import React, { useState, useRef, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Label } from "../atoms/texts/label";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Button } from "../molecules/buttons/button";
import { ExpandLessRounded } from "@mui/icons-material";

const MONTHS = [
    { key: "january", message: "global.months.january" },
    { key: "february", message: "global.months.february" },
    { key: "march", message: "global.months.march" },
    { key: "april", message: "global.months.april" },
    { key: "may", message: "global.months.may" },
    { key: "june", message: "global.months.june" },
    { key: "july", message: "global.months.july" },
    { key: "august", message: "global.months.august" },
    { key: "september", message: "global.months.september" },
    { key: "october", message: "global.months.october" },
    { key: "november", message: "global.months.november" },
    { key: "december", message: "global.months.december" },
];

function getMonthIndex(month) {
    // month peut être 1-12 ou "01"-"12"
    if (typeof month === "number") return month - 1;
    if (typeof month === "string") return parseInt(month, 10) - 1;
    return -1;
}

// Petit composant pour factoriser l'affichage d'une ligne de détail
function DetailGridRow({ message, value, valueProps, children }) {
    return (
        <div className="grid grid-cols-3 gap-firstDegree text-primaryStrong">
            <div className="col-span-2 truncate ">
                <Label message={message} />
            </div>
            <div className="w-full place-self-end truncate ">{children ? children : <Label fontWeight="semibold" {...valueProps} >{value}</Label>}</div>
        </div>
    );
}

export default function StatementTable({ data, dataOwner }) {
    const tableHeadClassName = "bg-primary text-white text-center rounded-t-lg h-8 m-2 p-firstDegree border border-4 border-lighter ";
    // Crée un tableau de 12 mois, chaque entrée = statementMonth ou undefined
    const monthsData = Array(12).fill(undefined);
    if (data?.statementMonths) {
        data.statementMonths.forEach((m) => {
            const idx = getMonthIndex(m.month);
            if (idx >= 0 && idx < 12) monthsData[idx] = m;
        });
    }

    // Calcul des totaux débit et crédit pour chaque mois
    const getMonthTotals = (m) => {
        if (!m) return { debit: 0, credit: 0 };

        const debit = m.overflowDebit || 0;
        const credit = m.overflowCredit || 0;

        // Vérification : ancienne méthode de calcul vs overflowDebit/overflowCredit
        const initialization = m.initialization || 0;
        const employerCost = m.employerCost || 0;
        const consultantServiceAmount = m.consultantServiceAmount || 0;
        const commission = m.commission || 0;
        const cooptation = m.cooptation || 0;

        const initializationDebit = initialization < 0 ? Math.abs(initialization) : 0;
        const initializationCredit = initialization > 0 ? initialization : 0;
        const legacyDebit = employerCost + initializationDebit;
        const legacyCredit = consultantServiceAmount - commission + initializationCredit + cooptation;

        if (legacyDebit !== debit) {
            console.log(
                `[StatementTable] Mois ${m.month} — écart débit`,
                {
                    methode: "legacyDebit = employerCost + initializationDebit",
                    calcul: `${employerCost} + ${initializationDebit} = ${legacyDebit}`,
                    valeurs: {
                        employerCost,
                        initialization,
                        initializationDebit,
                    },
                    overflowDebit: debit,
                },
                m
            );
        }
        if (legacyCredit !== credit) {
            console.log(
                `[StatementTable] Mois ${m.month} — écart crédit`,
                {
                    methode: "legacyCredit = consultantServiceAmount - commission + initializationCredit + cooptation",
                    calcul: `${consultantServiceAmount} - ${commission} + ${initializationCredit} + ${cooptation} = ${legacyCredit}`,
                    valeurs: {
                        consultantServiceAmount,
                        commission,
                        initialization,
                        initializationCredit,
                        cooptation,
                    },
                    overflowCredit: credit,
                },
                m
            );
        }

        return { debit, credit };
    };

    // Gestion de l'ouverture/fermeture des sous-lignes
    const [openMonthIdx, setOpenMonthIdx] = useState(null);
    const tableRef = useRef(null);

    // Fermer tout en cliquant en dehors du tableau
    useEffect(() => {
        function handleClickOutside(event) {
            if (tableRef.current && !tableRef.current.contains(event.target)) {
                setOpenMonthIdx(null);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <Table className="w-full bg-white/30 backdrop-blur-md" ref={tableRef}>
            <TableHeader>
                <TableRow className="">
                    <TableHead className={tableHeadClassName + " bg-transparent "}></TableHead>
                    <TableHead text="consultants.statement.overflow.tableHead.employerCost" className={tableHeadClassName + " w-1/2"} />
                    <TableHead text="consultants.statement.overflow.tableHead.services" className={tableHeadClassName + " w-1/2"} />
                </TableRow>
            </TableHeader>
            <TableBody>
                {MONTHS.map((month, idx) => {
                    const monthData = monthsData[idx];
                    const { debit, credit } = getMonthTotals(monthData);

                    // Vérifie si le mois est ouvrable
                    const isOpenable = !!(monthData && (debit !== 0 || credit !== 0));
                    const isOpen = openMonthIdx === idx;

                    return (
                        <React.Fragment key={month.key}>
                            <TableRow
                                className={
                                    (isOpenable
                                        ? "hover:bg-primary/5 cursor-pointer "
                                        : "cursor-default ") +
                                    "space-y-firstDegree bg-transparent " +
                                    (isOpen ? "bg-primary/10" : "")
                                }
                                onClick={
                                    isOpenable
                                        ? () => setOpenMonthIdx(isOpen ? null : idx)
                                        : undefined
                                }
                            >
                                <TableCell className="px-secondDegree py-firstDegree flex flex-row gap-firstDegree items-center justify-start no-wrap">
                                    <Label className="flex" message={month.message}>{month.message}</Label>
                                    <Button leftIcon={isOpen ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreIcon fontSize="small" />} showLeftIcon={isOpenable} style="ghost" sizePadding="small" showText={false} />
                                </TableCell>
                                <TableCell className="px-secondDegree py-firstDegree text-center">
                                    <Label className={debit === 0 ? "text-disabled" : ""}>
                                        {monthData
                                            ? debit.toLocaleString("fr-FR", {
                                                style: "currency",
                                                currency: monthData.currency || "EUR",
                                            })
                                            : "-"}
                                    </Label>
                                </TableCell>
                                <TableCell className="px-secondDegree py-firstDegree text-center">
                                    <Label className={credit === 0 ? "text-disabled" : ""}>
                                        {monthData
                                            ? credit.toLocaleString("fr-FR", {
                                                style: "currency",
                                                currency: monthData.currency || "EUR",
                                            })
                                            : "-"}
                                    </Label>
                                </TableCell>
                            </TableRow>
                            {isOpen && monthData && (
                                <TableRow className="bg-blue-100/30 hover:bg-blue-100/30">
                                    <TableCell />
                                    {/* Colonne DEBIT */}
                                    <TableCell colSpan={1} className="px-secondDegree py-1 align-top">
                                        <div className="flex flex-col gap-1 text-left">
                                            {debit === 0 ? (
                                                <Label className="text-disabled" message="consultants.statement.overview.noOperations" />
                                            ) : (
                                                <>
                                                    {[
                                                        { message: "consultants.statement.overview.initialization", value: monthData.initialization < 0 ? Math.abs(monthData.initialization) : undefined },
                                                        { message: "consultants.statement.overview.netSalary", value: monthData.netSalary },
                                                        { message: "consultants.statement.overview.employerContributions", value: monthData.employerContributions },
                                                        { message: "consultants.statement.overview.employeeCharge", value: monthData.employeeContributions },
                                                        { message: "consultants.statement.overview.taxes", value: monthData.taxes },
                                                        { message: "consultants.statement.overview.expenseAccount", value: monthData.expenses },
                                                        { message: "consultants.statement.overview.companyBenefits", value: monthData.companyBenefit },
                                                        { message: "consultants.statement.overview.investments", value: monthData.investmentsDebit },
                                                        { message: "consultants.statement.overview.otherOperations", value: monthData.otherOperationsDebit },
                                                    ].filter(item => item.value !== undefined && item.value !== 0).map((item) => (
                                                        <DetailGridRow
                                                            key={item.message}
                                                            message={item.message}
                                                            value={item.value?.toLocaleString("fr-FR", { style: "currency", currency: monthData.currency || "EUR" })}
                                                        />
                                                    ))}
                                                    <div className="grid grid-cols-3 gap-firstDegree text-primaryStrong border-t pt-1 mt-1">
                                                        <div className="col-span-2 truncate">
                                                            <Label fontWeight="bold" message="consultants.statement.overflow.tableHead.totalDebit" />
                                                        </div>
                                                        <div className="w-full place-self-end truncate">
                                                            <Label fontWeight="bold">
                                                                {debit.toLocaleString("fr-FR", { style: "currency", currency: monthData.currency || "EUR" })}
                                                            </Label>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </TableCell>
                                    {/* Colonne CREDIT */}
                                    <TableCell colSpan={1} className="px-secondDegree py-1 align-top">
                                        <div className="flex flex-col gap-1 text-left">
                                            {credit === 0 ? (
                                                <Label className="text-disabled" message="consultants.statement.overview.noOperations" />
                                            ) : (
                                                <>
                                                    {[
                                                        { message: "consultants.statement.overview.initialization", value: monthData.initialization > 0 ? monthData.initialization : undefined },
                                                        { message: "consultants.statement.overview.services_customer", value: monthData.consultantServiceAmount },
                                                        { message: "consultants.statement.overview.commission", value: monthData.commission, isNegative: true },
                                                        { message: "consultants.statement.overview.cooptations", value: monthData.cooptation },
                                                        { message: "consultants.statement.overview.investmentsCredit", value: monthData.investmentsCredit },
                                                        { message: "consultants.statement.overview.interestsPayment", value: monthData.interestsPayment },
                                                        { message: "consultants.statement.overview.otherOperationsCredit", value: monthData.otherOperationsCredit },

                                                    ].filter(item => item.value !== undefined && item.value !== 0).map((item) => (
                                                        item.isNegative ? (
                                                            <DetailGridRow
                                                                key={item.message}
                                                                message={item.message}
                                                                valueProps={{ className: "text-red-600" }}
                                                            >
                                                                <Label fontWeight="semibold" className="text-red-600">
                                                                    -{item.value?.toLocaleString("fr-FR", { style: "currency", currency: monthData.currency || "EUR" })}
                                                                </Label>
                                                            </DetailGridRow>
                                                        ) : (
                                                            <DetailGridRow
                                                                key={item.message}
                                                                message={item.message}
                                                                value={item.value?.toLocaleString("fr-FR", { style: "currency", currency: monthData.currency || "EUR" })}
                                                            />
                                                        )
                                                    ))}
                                                    <div className="grid grid-cols-3 gap-firstDegree text-primaryStrong border-t pt-1 mt-1">
                                                        <div className="col-span-2 truncate">
                                                            <Label fontWeight="bold" message="consultants.statement.overflow.tableHead.totalCredit" />
                                                        </div>
                                                        <div className="w-full place-self-end truncate">
                                                            <Label fontWeight="bold">
                                                                {credit.toLocaleString("fr-FR", { style: "currency", currency: monthData.currency || "EUR" })}
                                                            </Label>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </React.Fragment>
                    );
                })}
            </TableBody>
        </Table>
    );
}